import { useEffect, useMemo, useState, type JSX, type MouseEvent } from 'react';
import { Button, Menu, MenuItem, ListItemText, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useWorkspaceStore, availableWorkspacesFor, canSwitchWorkspace } from '@store/workspace.store';
import { useEffectiveModuleCodes } from '@hooks/useEffectiveModuleCodes';

export function WorkspaceSwitcher(): JSX.Element {
  const currentWorkspaceId = useWorkspaceStore((state) => state.currentWorkspaceId);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  // The token's codes plus Modifications for a selected correction approver only — see `useEffectiveModuleCodes`.
  const moduleCodes = useEffectiveModuleCodes();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Memoized on the claim itself, not recomputed per render — the array is a
  // useEffect dependency below, and a fresh identity every render would make
  // that effect fire on every render.
  const workspaces = useMemo(() => availableWorkspacesFor(moduleCodes), [moduleCodes]);
  const current = workspaces.find((workspace) => workspace.id === currentWorkspaceId);

  // The store is persisted to localStorage and is NOT per-user, so a
  // workspace chosen by whoever logged in here last survives into the next
  // session. Left alone, a receptionist inherits "Laboratory" — and with it
  // the full sidebar tree, since only the Front Desk workspace declares the
  // flat rail. Snapping to the first workspace they actually hold is what
  // makes the switch honest for the account currently signed in.
  useEffect(() => {
    if (!current && workspaces[0]) setWorkspace(workspaces[0].id);
  }, [current, workspaces, setWorkspace]);

  // One workspace is not a choice. Rendering a dropdown that opens onto a
  // single item advertises somewhere else to go and there is nowhere else.
  //
  // THE SECOND CLAUSE NO LONGER HIDES A REAL CHOICE (desk, 2026-09-08). It used
  // to withhold the control from everyone without ADMINISTRATION "even when they
  // hold several workspaces", which pinned a user granted Front Desk, Main
  // Counter and IP Billing to whichever one localStorage held. `canSwitchWorkspace`
  // now also passes anyone holding more than one of their OWN workspaces — see
  // its note for why that does not re-open the door it was built to close.
  // The snapping effect above still runs, so the label is never a workspace
  // they don't hold.
  if (workspaces.length <= 1 || !canSwitchWorkspace(moduleCodes)) {
    return (
      <Typography variant="body2" sx={{ px: 1, fontWeight: 500 }}>
        {current?.label ?? workspaces[0]?.label ?? 'Workspace'}
      </Typography>
    );
  }

  return (
    <>
      <Button
        size="small"
        color="inherit"
        endIcon={<ExpandMoreIcon />}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        sx={{ textTransform: 'none' }}
      >
        {current?.label ?? 'Workspace'}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {workspaces.map((workspace) => (
          <MenuItem
            key={workspace.id}
            selected={workspace.id === currentWorkspaceId}
            onClick={() => {
              setWorkspace(workspace.id);
              setAnchorEl(null);
            }}
          >
            <ListItemText>{workspace.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
